import { keepPreviousData, useQuery, useQueryClient } from '@tanstack/react-query'
import { useState } from 'react'
import { cmdbHealthListQuery, cmdbHealthQuery, cmdbVerify } from '../api/cmdb'
import { ApiError } from '../api/client'
import { toast } from '../ui/notify'
import { moduleOf, shellQuery } from '../api/shell'
import { Forbidden } from '../ui/Forbidden'
import { Content } from '../ui/Shell'
import { Spinner } from '../ui/Spinner'
import { usePageStyles } from '../ui/usePageStyles'
import { CmdbViews } from './CmdbViews'

/**
 * `Cmdb::health` - sanatatea registrului, la /app/cmdb/health.
 *
 * Un CMDB nu se strica zgomotos. Se strica tacut: elemente pe care nimeni nu le
 * mai actualizeaza, servicii fara dependente scrise. Datele devin gresite
 * continuand sa arate complete - de-aia ecranul asta numara tocmai ce nu se
 * vede, si de-aia sta langa CMDB, nu langa descoperire.
 *
 * Adresa e cea din portal: catre `/cmdb/health` trimit patru linkuri din
 * ferestrele de mentenanta (Inventoree), pastila "Not seen for N days" din
 * tabloul ADM (Softsee) si textul de pe candidatii de problema (Easeedesk) -
 * unele cu ancora `#admChanges`, care de-aia si-a pastrat id-ul.
 *
 * MACHETA E CEA A PORTALULUI
 *
 * `.adm-tiles-6 > .dashboard-stat` cu `.visual` / `.details` / `.more`, apoi
 * cate o `.adm-section` cu `.adm-card` sub ea. Clasele si tonurile sunt cele
 * din `adm.css`, si nu unele noi: o pastila cu alt contur s-ar citi ca alt fel
 * de informatie, cand de fapt e acelasi raspuns intr-un alt ecran.
 *
 * FARA BARA DE FILTRE
 *
 * Serverul citeste numai clientul ales in bara de sus. Un control care nu
 * schimba nimic e mai rau decat niciun control.
 */

const STYLES = ['css/adm.css']
const PAGE = 25

/**
 * Cele opt constatari. `id` e ancora catre care trimit celelalte ecrane -
 * `#admChanges` e scrisa in patru machete de mentenanta, deci nu se schimba.
 */
const SECTIONS: {
  kind: string
  id: string
  tone: string
  icon: string
  tile: string
  title: string
  note: string
  empty: string
  headers: string[]
}[] = [
  {
    kind: 'duplicates',
    id: 'admDuplicates',
    tone: 'red-intense',
    icon: 'fa-clone',
    tile: 'Possible duplicates',
    title: 'Possible duplicates',
    note: 'flagged by the identification rules, not yet resolved',
    empty: 'No duplicates outstanding.',
    headers: ['Item', 'Looks like', 'Customer', 'Confidence'],
  },
  {
    kind: 'stale',
    id: 'admStale',
    tone: 'yellow',
    icon: 'fa-clock',
    tile: 'Unconfirmed',
    title: 'Nobody has confirmed these',
    /*
     * Doua feluri de tacere, si se spune care e care. Un echipament tace pentru
     * ca agentul a murit sau masina a plecat. Un serviciu de business nu e
     * vazut NICIODATA de niciun releu: el nu tace, doar n-a mai spus nimeni ca
     * harta lui mai e adevarata.
     */
    note: 'endpoints, after 30 days without reporting; services and applications, after 90 days without anyone confirming them',
    empty: 'Everything has reported or been confirmed recently.',
    headers: ['Item', 'Class', 'Customer', 'Waiting for', 'Days quiet', 'Confirm'],
  },
  {
    kind: 'orphan',
    id: 'admOrphan',
    tone: 'blue-hoki',
    icon: 'fa-unlink',
    tile: 'Nothing connected',
    title: 'Nothing connected',
    note: 'not a fault for a standalone workstation; for a server it means the machine never reported its connections',
    empty: 'Everything has at least one relation.',
    headers: ['Item', 'Class', 'Customer', 'Importance'],
  },
  {
    kind: 'incomplete',
    id: 'admIncomplete',
    tone: 'grey',
    icon: 'fa-user-slash',
    tile: 'No owner or importance',
    title: 'No owner or importance',
    note: 'neither can be discovered; both are what a change board asks for first',
    empty: 'Everything has an owner and an importance.',
    headers: ['Item', 'Class', 'Customer', 'Importance'],
  },
  {
    kind: 'unowned',
    id: 'admUnowned',
    tone: 'red',
    icon: 'fa-phone-slash',
    tile: 'Services nobody answers for',
    title: 'Services nobody answers for',
    /*
     * Separata de "No owner or importance", si nu din exces de liste: aia
     * numara si cele patru mii de statii fara proprietar, iar intr-o lista de
     * patru mii nu se vede ca serviciul de plati n-are pe nimeni.
     */
    note: 'services only, and only those that inherit no owner from higher up the graph: if these go down, there is no telling who to call',
    empty: 'Every service has an owner, its own or inherited.',
    headers: ['Item', 'Class', 'Customer', 'Importance'],
  },
  {
    kind: 'changes',
    id: 'admChanges',
    tone: 'red-intense',
    icon: 'fa-exclamation-triangle',
    tile: 'Unauthorised changes',
    title: 'Changes nobody approved',
    note: 'recorded against an item with no change request covering them',
    empty: 'Every recorded change is accounted for.',
    headers: ['When', 'Item', 'What changed', 'From', 'To'],
  },
  {
    kind: 'forbidden',
    id: 'admForbidden',
    tone: 'red',
    icon: 'fa-project-diagram',
    tile: 'Connections the blueprint forbids',
    title: 'Connections the blueprint forbids',
    /*
     * Vocabularul (Settings -> CI rules) se verifica azi la SCRIERE, si numai pe
     * un singur drum - cel din promovare. Corelatorul, rezolvatorul de
     * aplicatii, importul de topologie si mana omului nu trec pe acolo, deci
     * graful contine muchii pe care nu le-a permis nimeni.
     */
    note: 'live edges the class vocabulary does not allow; impact analysis and SLA rollup climb them all the same',
    empty: 'Every link in the graph is allowed by the class blueprint.',
    headers: ['From', 'To', 'Relation', 'Written by', 'Customer'],
  },
  {
    kind: 'required',
    id: 'admRequired',
    tone: 'grey',
    icon: 'fa-clipboard-list',
    tile: 'Required properties left empty',
    title: 'Required properties left empty',
    /* Macheta afirma ceva ce datele nu sustin. Se numara ELEMENTE, nu campuri:
       „opt elemente carora le lipseste ceva" e o lista pe care o rezolva
       cineva. */
    note: 'the class says the property is required, and the item does not have it',
    empty: 'Every item has what its class asks for.',
    headers: ['Item', 'Class', 'Customer', 'Missing', 'How many', 'Importance'],
  },
]

export function CmdbHealthPage() {
  const shell = useQuery(shellQuery(moduleOf('/cmdb')))
  usePageStyles(STYLES, shell.data?.version ?? '')
  const page = useQuery(cmdbHealthQuery)

  if (page.error instanceof ApiError && page.error.status === 403) return <Forbidden />
  if (!page.data) return <Spinner />

  const summary = page.data.summary

  return (
    /*
     * Fara inca un `<div id="content">`: invelisul ala il pune deja `<Content>`
     * din Shell, si asa il are si `/app/cmdb` - de-aia banda statea acolo in
     * alt loc decat aici, desi e aceeasi componenta.
     *
     * Doua elemente cu acelasi id nu sunt doar o greseala de markup: `adm.css`
     * deseneaza pe `#content` spalarea de fundal a modulului (doua gradiente
     * radiale), iar doua cutii una in alta o pictau de doua ori, deci banda
     * sedea pe un gri mai apasat decat pe lista. Aceeasi regula ca pe fisa -
     * vezi `CmdbRecordPage`, unde e si proba care o tine.
     */
    <Content>
      {/* Banda celor patru vederi, inaintea pastilelor ca in portal. */}
      <CmdbViews views={page.data.views} current="cmdb/health" />

      {/*
        * OPT pastile, deci patru pe rand si doua randuri: `col-md-3` e chiar
        * sfertul pentru care a fost desenata pastila platformei. `adm-tiles-6`
        * nu se mai pune - la 16,66% ar fi iesit sase pe primul rand si doua
        * pe al doilea, si a doua constatare noua ar fi parut a alta sectiune.
        */}
      <div className="row adm-tiles">
        {SECTIONS.map((section) => (
          <div className="col-md-3 col-sm-6" key={section.kind}>
            <div className={`dashboard-stat ${section.tone}`}>
              <div className="visual">
                <i className={`fas ${section.icon}`}></i>
              </div>
              <div className="details">
                <div className="number">{Number(summary[section.kind] ?? 0).toLocaleString('en-US')}</div>
                <div className="desc">{section.tile}</div>
              </div>
              <a className="more" href={`#${section.id}`}>
                See them <i className="fa fa-arrow-circle-right"></i>
              </a>
            </div>
          </div>
        ))}
      </div>

      {SECTIONS.map((section) => (
        <Section
          key={section.kind}
          section={section}
          count={Number(summary[section.kind] ?? 0)}
          canAttest={page.data.canAttest && summary.can_attest === true}
        />
      ))}
    </Content>
  )
}

function Section({
  section,
  count,
  canAttest,
}: {
  section: (typeof SECTIONS)[number]
  count: number
  canAttest: boolean
}) {
  const [start, setStart] = useState(0)
  const query = useQueryClient()
  /*
   * Lista se cere numai cand cifra spune ca e ceva de aratat. Sase liste cerute
   * la deschidere ar fi opt interogari grele pentru opt tabele care in cel mai
   * bun caz - un registru curat - sunt toate goale.
   */
  const list = useQuery({
    ...cmdbHealthListQuery(section.kind, start, PAGE),
    enabled: count > 0,
    placeholderData: keepPreviousData,
  })
  const rows = list.data?.data ?? []
  const total = list.data?.recordsTotal ?? count

  return (
    <>
      <div className={`adm-section adm-section-${section.tone}`} id={section.id}>
        <h4>
          <i className={`fas ${section.icon}`}></i> {count.toLocaleString('en-US')} {section.title}
        </h4>
        <span className="adm-section-note">{section.note}</span>
      </div>

      <div className="adm-card">
        {count === 0 ? (
          <div className="adm-empty-inline">
            <i className="fa fa-check-circle"></i>
            {section.empty}
          </div>
        ) : list.isFetching && rows.length === 0 ? (
          <Spinner />
        ) : (
          <table className="table table-striped table-hover table-bordered">
            <thead>
              <tr>
                {section.headers.map((header) => (
                  <th key={header} className={header === 'Days quiet' || header === 'Confidence' || header === 'How many' ? 'adm-numeric' : undefined}>
                    {header}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {rows.map((row, index) => (
                <Row
                  key={index}
                  kind={section.kind}
                  row={row}
                  canAttest={canAttest}
                  onConfirmed={() => void query.invalidateQueries({ queryKey: ['cmdb', 'health'] })}
                />
              ))}
            </tbody>
          </table>
        )}
      </div>

      {total > PAGE && (
        <div className="text-right" style={{ marginBottom: 12 }}>
          <button className="btn btn-xs btn-default" disabled={start === 0} onClick={() => setStart(Math.max(0, start - PAGE))}>
            Previous
          </button>{' '}
          <span className="adm-muted">
            {start + 1}&ndash;{Math.min(start + PAGE, total)} of {total}
          </span>{' '}
          <button className="btn btn-xs btn-default" disabled={start + PAGE >= total} onClick={() => setStart(start + PAGE)}>
            Next
          </button>
        </div>
      )}
    </>
  )
}

function Row({
  kind,
  row,
  canAttest,
  onConfirmed,
}: {
  kind: string
  row: Record<string, unknown>
  canAttest: boolean
  onConfirmed(): void
}) {
  const text = (key: string) => (row[key] === null || row[key] === undefined ? '' : String(row[key]))

  if (kind === 'duplicates') {
    return (
      <tr>
        <td>
          <a href={`/app/cmdb/view/${Number(row.a_id)}`}>{text('a_name')}</a>
        </td>
        <td>
          <a href={`/app/cmdb/view/${Number(row.b_id)}`}>{text('b_name')}</a>
        </td>
        <td className="adm-muted">{text('company_name')}</td>
        <td className="adm-numeric">{text('score')}</td>
      </tr>
    )
  }
  if (kind === 'changes') {
    return (
      <tr>
        <td className="adm-nowrap adm-muted">{text('detected_at').slice(0, 16)}</td>
        <td>
          <a href={`/app/cmdb/view/${Number(row.ci_id)}`}>{text('ci_name')}</a>
        </td>
        <td>{text('field_key')}</td>
        <td className="adm-muted">{text('old_value')}</td>
        <td>{text('new_value')}</td>
      </tr>
    )
  }
  if (kind === 'forbidden') {
    return (
      <tr>
        <td>
          <a href={`/app/cmdb/view/${Number(row.source_ci_id)}`}>{text('source_name')}</a>{' '}
          <span className="adm-muted">{text('source_class')}</span>
        </td>
        <td>
          <a href={`/app/cmdb/view/${Number(row.target_ci_id)}`}>{text('target_name')}</a>{' '}
          <span className="adm-muted">{text('target_class')}</span>
        </td>
        <td className="adm-muted">{text('relation_name')}</td>
        {/*
         * Cine a scris muchia hotaraste ce se face cu ea. Una venita de la
         * corelator e o regula de reglat. Una scrisa de mana e o afirmatie a
         * unui om care contrazice vocabularul, si aia cere o hotarare - nu
         * aceeasi treaba deloc.
         */}
        <td>{writtenBy(row.origin)}</td>
        <td className="adm-muted">{text('company_name')}</td>
      </tr>
    )
  }
  if (kind === 'required') {
    return (
      <tr>
        <td>
          <a href={`/app/cmdb/view/${Number(row.ID)}`}>{text('name')}</a>{' '}
          {Number(row.kind) >= 3 ? <span className="adm-pill adm-pill-warn">Service</span> : null}
        </td>
        <td className="adm-muted">{text('class_name')}</td>
        <td className="adm-muted">{text('company_name')}</td>
        <td>{text('missing_fields')}</td>
        <td className="adm-numeric">
          <span className="adm-pill">{Number(row.missing_count)}</span>
        </td>
        <td>{importance(row.criticality)}</td>
      </tr>
    )
  }
  if (kind === 'stale') {
    return (
      <tr>
        <td>
          <a href={`/app/cmdb/view/${Number(row.ID)}`}>{text('name')}</a>
        </td>
        <td className="adm-muted">{text('class_name')}</td>
        <td className="adm-muted">{text('company_name')}</td>
        {/* Ce ASTEAPTA randul: un semn de la releu, sau o confirmare de la om.
            Cele doua se repara altfel, deci ecranul nu cere aceeasi fapta
            pentru amandoua. */}
        <td className="adm-muted">{Number(row.asserted) === 1 ? 'a person to confirm it' : 'the agent to report'}</td>
        <td className="adm-numeric">{text('days_quiet')}</td>
        <td className="text-right">
          {/*
           * Butonul apare NUMAI pe randurile care asteapta un om. Un element
           * care asteapta agentul nu se repara spunand ca e adevarat - se
           * repara pornind agentul - iar un buton acolo ar invita pe cineva sa
           * ascunda o problema in loc s-o rezolve.
           */}
          {canAttest && Number(row.asserted) === 1 ? (
            <Confirm id={Number(row.ID)} onDone={onConfirmed} />
          ) : (
            <span className="adm-muted">&mdash;</span>
          )}
        </td>
      </tr>
    )
  }
  return (
    <tr>
      <td>
        <a href={`/app/cmdb/view/${Number(row.ID)}`}>{text('name')}</a>
      </td>
      <td className="adm-muted">{text('class_name')}</td>
      <td className="adm-muted">{text('company_name')}</td>
      <td>{importance(row.criticality)}</td>
    </tr>
  )
}

function Confirm({ id, onDone }: { id: number; onDone(): void }) {
  const [busy, setBusy] = useState(false)
  return (
    <button
      className="btn btn-xs btn-default"
      disabled={busy}
      title="Say that this item is still true. It does not touch what discovery reports."
      onClick={() => {
        setBusy(true)
        void cmdbVerify(id)
          .then((result) => {
            setBusy(false)
            if (result.status !== 'OK') {
              toast(result.message, 'CMDB', 'error')
              return
            }
            toast(result.message, 'CMDB')
            onDone()
          })
          .catch(() => {
            setBusy(false)
            toast('That could not be saved.', 'CMDB', 'error')
          })
      }}
    >
      <i className="fa fa-check"></i> Still true
    </button>
  )
}

/** `origin` din `it_ci_relations`, cu pastilele din `cmdb_health.js`. */
function writtenBy(value: unknown) {
  const origin = Number(value)
  if (origin === 1) return <span className="adm-pill adm-pill-warn">by hand</span>
  if (origin === 2) return 'discovery'
  if (origin === 3) return 'rule'
  return <span className="adm-muted">&mdash;</span>
}

/** `adm_importance()` - aceleasi cinci trepte si acelasi "not set". */
function importance(value: unknown) {
  const level = value === null || value === undefined || value === '' ? 0 : Number(value)
  if (level < 1 || level > 5) return <span className="adm-imp adm-imp-0">not set</span>
  const labels: Record<number, string> = { 5: 'Critical', 4: 'Important', 3: 'Standard', 2: 'Minor', 1: 'Negligible' }
  return <span className={`adm-imp adm-imp-${level}`}>{labels[level]}</span>
}
